import { Badge } from 'app/components/ui/badge'
import { Button } from 'app/components/ui/button'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from 'app/components/ui/sheet'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import { formatCurrency } from 'lib/formatCurrency'
import type { Family, FamilyPerson } from 'app/types/related-contacts'
import { recipientFor } from 'lib/relatedContacts'

function FamilyList({ family, current }: { family: Family; current: FamilyPerson }) {
  const people = [...(family.primary ? [family.primary] : []), ...family.members]
  return (
    <section className="border-t border-default px-6 py-4">
      <h3 className="mb-2 text-body-sm font-semibold text-strong">Family ({people.length})</h3>
      <ul>
        {people.map((person) => (
          <li
            key={person.id}
            className="flex items-center justify-between gap-2 border-b border-default py-2 text-body-sm last:border-0"
          >
            <span>
              <span className="font-medium text-strong">{person.name.split(` `)[0]}</span>{' '}
              <span className="text-subtle">
                {person.id === family.primary?.id
                  ? `primary contact`
                  : [(person.age ?? 18) < 18 ? person.age : undefined, person.relationship].filter(Boolean).join(`, `)}
                {person.id === current.id && ` (this contact)`}
              </span>
            </span>
            {person.leadStatus ? (
              <Badge label={person.leadStatus === `Scheduled` ? `Booked` : person.leadStatus} color="gray" />
            ) : (
              <span className="text-minimal">Not a lead</span>
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}

function Composer({ to, about }: { to?: FamilyPerson; about?: FamilyPerson }) {
  if (!to) {
    return (
      <div className="m-6 rounded-lg border border-dashed border-strong p-3 text-body-sm text-subtle">
        Texting is blocked: this contact is under 18 and no adult is linked. Link a parent or guardian to reach them.
      </div>
    )
  }
  const aboutOther = about && about.id !== to.id
  return (
    <div className="border-t border-default bg-surface-subtle px-6 py-4">
      <p className="mb-2 text-body-xs text-subtle">
        Text to <span className="font-semibold text-strong">{to.name}</span>
        {aboutOther && (
          <>
            , about <span className="font-semibold text-strong">{about.name.split(` `)[0]}</span>
          </>
        )}
      </p>
      <div className="rounded-lg border border-default bg-surface p-3 text-body-sm text-default">
        Hi {to.name.split(` `)[0]}, it&apos;s Smile Dental.{' '}
        {aboutOther ? `Following up about ${about.name.split(` `)[0]}'s visit...` : `Following up about your visit...`}
      </div>
      <p className="mt-2 text-body-xs text-minimal">Prototype: nothing is sent.</p>
    </div>
  )
}

export function DemoPersonSheet({
  selection,
  onClose,
  onOpen,
}: {
  selection?: { person: FamilyPerson; family: Family }
  onClose: () => void
  onOpen: (person: FamilyPerson, family: Family) => void
}) {
  const person = selection?.person
  const family = selection?.family
  return (
    <Sheet open={!!selection} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        className="w-full overflow-y-auto sm:max-w-md"
        data-testid="related-contacts-demo-sheet"
      >
        {person && family && <PersonView person={person} family={family} onOpen={onOpen} />}
      </SheetContent>
    </Sheet>
  )
}

function PersonView({
  person,
  family,
  onOpen,
}: {
  person: FamilyPerson
  family: Family
  onOpen: (person: FamilyPerson, family: Family) => void
}) {
  const isPrimary = family.primary?.id === person.id
  const recipient = recipientFor(person, family)
  const others = family.members.filter((m) => m.id !== person.id)

  return (
    <>
      <SheetHeader>
        <SheetTitle>{person.name}</SheetTitle>
        <SheetDescription asChild>
          <div className="flex flex-wrap items-center gap-2 text-body-sm text-subtle">
            {isPrimary && family.primary && (
              <>
                <Badge label="Primary contact" color="blue" />
                <span>because {family.primary.reason}</span>
              </>
            )}
            {!isPrimary &&
              [(person.age ?? 18) < 18 ? `${person.age}` : undefined, person.relationship].filter(Boolean).join(`, `)}
            {person.leadStatus && (
              <Badge label={person.leadStatus === `Scheduled` ? `Booked` : person.leadStatus} color="gray" />
            )}
            {person.openTreatment > 0 && (
              <Badge label={`${formatCurrency(person.openTreatment)} open treatment`} color="gray" />
            )}
          </div>
        </SheetDescription>
      </SheetHeader>

      {recipient.kind === `primary` && (
        <div
          className="mx-6 mb-4 rounded-lg border border-primary-200 bg-primary-alpha-8 p-3 text-body-sm"
          data-testid="reach-through"
        >
          <p className="font-semibold text-strong">Reach through {recipient.person.name}</p>
          <p className="text-subtle">
            Primary contact, because {family.primary?.reason} · {formatPhoneNumber(recipient.person.phone)}
          </p>
          <p className="text-subtle">
            {(person.age ?? 18) < 18
              ? `${person.name.split(` `)[0]} is under 18, so calls and texts go to ${recipient.person.name.split(` `)[0]}.`
              : `${person.name.split(` `)[0]} shares ${recipient.person.name.split(` `)[0]}'s number, so texts go to ${recipient.person.name.split(` `)[0]}.`}
          </p>
          <div className="mt-1 flex gap-3">
            <Button variant="link" size="inline" onClick={() => family.primary && onOpen(family.primary, family)}>
              Open {recipient.person.name.split(` `)[0]}
            </Button>
            <Button variant="link" size="inline" disabled>
              Change primary contact
            </Button>
          </div>
        </div>
      )}

      {recipient.kind === `self` && !isPrimary && family.primary && (
        <p className="mx-6 mb-4 text-body-sm text-subtle">
          Messaged directly at {formatPhoneNumber(person.phone)}. Still shown in the family of {family.primary.name}{' '}
          (primary contact).
        </p>
      )}

      {isPrimary && others.length > 0 && (
        <section className="border-t border-default px-6 py-4">
          <h3 className="mb-2 text-body-sm font-semibold text-strong">Contacting {person.name.split(` `)[0]} about</h3>
          <ul>
            {others.map((member) => (
              <li
                key={member.id}
                className="flex items-center justify-between gap-2 border-b border-default py-2 text-body-sm last:border-0"
              >
                <Button variant="link" size="inline" onClick={() => onOpen(member, family)}>
                  {member.name}
                </Button>
                <span className="text-subtle">
                  {member.leadStatus === `Scheduled`
                    ? `Booked`
                    : (member.nextAction ?? member.leadStatus ?? `Not a lead`)}
                  {member.openTreatment > 0 && ` · ${formatCurrency(member.openTreatment)}`}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {(family.members.length > 0 || !family.primary) && <FamilyList family={family} current={person} />}

      <Composer to={recipient.kind === `none` ? undefined : recipient.person} about={person} />
    </>
  )
}
